/**
 * Currency Service
 * Handles all FX rate related API calls
 */
import api from './api';

/**
 * Get user-specific cache key
 * Each user has their own currency preference cache
 */
const getUserCacheKey = () => {
  try {
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    const userId = user.id || 'guest';
    return `fx_preference_cache_${userId}`;
  } catch (error) {
    console.error('Error getting user ID:', error);
    return 'fx_preference_cache_guest';
  }
};

/**
 * Get user-specific selected currency key
 */
const getUserCurrencyKey = () => {
  try {
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    const userId = user.id || 'guest';
    return `selected_currency_${userId}`;
  } catch (error) {
    console.error('Error getting user ID:', error);
    return 'selected_currency_guest';
  }
};

/**
 * Get initial currency from sessionStorage synchronously
 * Used to prevent USD flicker on page load
 */
export const getInitialCurrency = () => {
  try {
    const currencyKey = getUserCurrencyKey();
    const savedCurrency = sessionStorage.getItem(currencyKey);
    if (savedCurrency) {
      console.log('🎯 [FX] Initial currency from sessionStorage:', savedCurrency);
      return savedCurrency;
    }
  } catch (error) {
    console.error('Error getting initial currency:', error);
  }
  return 'USD';
};

const CACHE_DURATION = 3600000; // 1 hour in milliseconds

/**
 * Get cached currency preference from sessionStorage
 * Uses user-specific cache key
 */
const getCachedPreference = () => {
  try {
    const cacheKey = getUserCacheKey();
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      const { currency, rate, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_DURATION) {
        return { currency, rate };
      }
    }
  } catch (error) {
    console.error('Error reading cached preference:', error);
  }
  return null;
};

/**
 * Cache currency preference in sessionStorage
 * Uses user-specific cache key
 */
const setCachedPreference = (currency, rate) => {
  try {
    const cacheKey = getUserCacheKey();
    sessionStorage.setItem(cacheKey, JSON.stringify({
      currency,
      rate,
      timestamp: Date.now()
    }));
  } catch (error) {
    console.error('Error caching preference:', error);
  }
};

/**
 * Clear cached currency preference for current user
 */
export const clearCurrencyCache = () => {
  try {
    const cacheKey = getUserCacheKey();
    sessionStorage.removeItem(cacheKey);
  } catch (error) {
    console.error('Error clearing currency cache:', error);
  }
};

/**
 * Get list of available currencies
 */
export const getCurrencies = async () => {
  try {
    const response = await api.get('/fx/currencies');
    console.log('✅ [FX] Currencies response:', response);
    return response.data;
  } catch (error) {
    console.warn('⚠️ [FX] Failed to fetch currencies from backend, using fallback:', error.message);
    // Fallback to hardcoded list
    return {
      currencies: ['USD', 'SGD', 'EUR', 'GBP', 'JPY', 'AUD', 'HKD', 'CNY', 'AED', 'INR']
    };
  }
};

/**
 * Get user's currency preference
 * Uses cache to minimize API calls
 * @param {boolean} useCache - Whether to use cached data (default: true)
 * @param {boolean} forceRefresh - Force bypass cache and fetch fresh rates (default: false)
 */
export const getCurrencyPreference = async (useCache = true, forceRefresh = false) => {
  // Skip cache if force refresh is requested
  if (!forceRefresh && useCache) {
    const cached = getCachedPreference();
    if (cached) {
      console.log('✅ [FX] Using cached preference:', cached);
      return cached;
    }
  }
  
  // Clear cache if force refresh
  if (forceRefresh) {
    console.log('🔄 [FX] Force refresh requested, clearing cache');
    clearCurrencyCache();
  }

  try {
    // Try to get preference from backend
    const response = await api.get('/fx/preference');
    console.log('✅ [FX] Preference response:', response);
    
    if (response.data && response.data.currency) {
      const { currency, rate } = response.data;
      
      // Cache the result
      setCachedPreference(currency, rate);
      
      return { currency, rate };
    }
  } catch (error) {
    console.warn('⚠️ [FX] Failed to fetch preference from /fx/preference, falling back to rates:', error.message);
  }

  // Fallback: Get currency from sessionStorage or default to USD
  try {
    const currencyKey = getUserCurrencyKey();
    const savedCurrency = sessionStorage.getItem(currencyKey) || 'USD';
    console.log('📍 [FX] Using saved currency for current user:', savedCurrency);
    
    // Fetch rates to get the conversion rate
    const ratesResponse = await api.get('/fx/rates');
    console.log('✅ [FX] Rates response:', ratesResponse);
    
    if (ratesResponse.data && ratesResponse.data.rates) {
      const rates = ratesResponse.data.rates;
      const rateValue = rates[savedCurrency] || 1;
      
      const rateObj = {
        rate: rateValue,
        rateWithMarkup: rateValue // Already includes markup from backend
      };
      
      // Cache the result
      setCachedPreference(savedCurrency, rateObj);
      
      console.log('✅ [FX] Constructed preference:', { currency: savedCurrency, rate: rateObj });
      
      return { currency: savedCurrency, rate: rateObj };
    }
  } catch (error) {
    console.error('❌ [FX] Failed to fetch rates:', error);
  }

  // Final fallback to USD
  console.warn('⚠️ [FX] All methods failed, falling back to USD');
  return { currency: 'USD', rate: { rate: 1, rateWithMarkup: 1 } };
};

/**
 * Save user's currency preference
 * Uses user-specific sessionStorage key
 */
export const saveCurrencyPreference = async (currency) => {
  try {
    // Save to user-specific sessionStorage key
    const currencyKey = getUserCurrencyKey();
    sessionStorage.setItem(currencyKey, currency);
    console.log('💾 [FX] Saved currency to sessionStorage for current user:', currency);
    
    // Try to save to backend
    try {
      const response = await api.post('/fx/preference', { currency });
      console.log('✅ [FX] Saved preference to backend:', response);
      
      if (response.data && response.data.rate) {
        const { rate } = response.data;
        setCachedPreference(currency, rate);
        return { currency, rate };
      }
    } catch (backendError) {
      console.warn('⚠️ [FX] Backend save failed, using rates fallback:', backendError.message);
    }
    
    // Fallback: Fetch rate from /fx/rates
    const ratesResponse = await api.get('/fx/rates');
    console.log('✅ [FX] Fetched rates for fallback:', ratesResponse);
    
    if (ratesResponse.data && ratesResponse.data.rates) {
      const rates = ratesResponse.data.rates;
      const rateValue = rates[currency] || 1;
      
      const rateObj = {
        rate: rateValue,
        rateWithMarkup: rateValue // Already includes markup from backend
      };
      
      // Update cache
      setCachedPreference(currency, rateObj);
      
      console.log('✅ [FX] Constructed rate object:', { currency, rate: rateObj });
      
      return { currency, rate: rateObj };
    }
    
    throw new Error('Failed to fetch rates');
  } catch (error) {
    console.error('❌ [FX] Failed to save currency preference:', error);
    throw error;
  }
};

/**
 * Convert amount from USD to target currency
 */
export const convertCurrency = async (amountUSD, targetCurrency) => {
  try {
    const response = await api.post('/fx/convert', {
      amountUSD,
      targetCurrency
    });
    return response.data;
  } catch (error) {
    console.error('Failed to convert currency:', error);
    throw error;
  }
};

/**
 * Get current FX rates
 */
export const getFXRates = async () => {
  try {
    const response = await api.get('/fx/rates');
    return response.data;
  } catch (error) {
    console.error('Failed to fetch FX rates:', error);
    throw error;
  }
};
